"use client"

import type React from "react"
import { useState } from "react"
import { useRouter } from "next/navigation"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp"
import { MessageCircle, ArrowLeft } from "lucide-react"

// Connexion par code WhatsApp. Deux écrans, un seul objectif à la fois
// (a11y dys/TDAH) : 1) saisie du numéro, 2) saisie du code à 6 chiffres.
// L'onglet Email reste inchangé : ce composant est autonome.

interface WaLoginFormProps {
  onSuccess: () => void
}

type Step = "phone" | "code"

export function WaLoginForm({ onSuccess }: WaLoginFormProps) {
  const router = useRouter()
  const [step, setStep] = useState<Step>("phone")
  const [phone, setPhone] = useState("")
  const [code, setCode] = useState("")
  const [info, setInfo] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const requestCode = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setInfo(null)
    setIsLoading(true)
    try {
      const res = await fetch("/api/auth/wa/request-code", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone }),
      })
      const data = await res.json().catch(() => ({}))

      if (res.status === 429) {
        setError(data.message || "Trop de demandes. Réessaie plus tard.")
        return
      }
      if (!res.ok) {
        setError(
          data.error === "phone_invalide" || data.error === "phone_manquant"
            ? "Numéro invalide. Saisis-le au format international (ex. +33 6 12 34 56 78)."
            : "Service indisponible. Réessaie dans un instant.",
        )
        return
      }

      // Réponse neutre (anti-énumération) : on passe TOUJOURS à l'écran code.
      setInfo(data.message || "Si ce numéro est éligible, un code vient d'être envoyé sur WhatsApp.")
      setCode("")
      setStep("code")
    } catch {
      setError("Connexion impossible. Vérifie ta connexion internet.")
    } finally {
      setIsLoading(false)
    }
  }

  const verifyCode = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setIsLoading(true)
    try {
      const res = await fetch("/api/auth/wa/verify-code", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone, code }),
      })
      const data = await res.json().catch(() => ({}))

      if (res.ok && data.ok) {
        onSuccess()
        router.push(typeof data.redirect === "string" ? data.redirect : "/devoirs")
        router.refresh()
        return
      }

      if (res.status === 429) {
        setError(data.message || "Trop de tentatives. Demande un nouveau code.")
        return
      }
      setError(data.message || "Code invalide ou expiré.")
    } catch {
      setError("Connexion impossible. Vérifie ta connexion internet.")
    } finally {
      setIsLoading(false)
    }
  }

  if (step === "code") {
    return (
      <form onSubmit={verifyCode} className="space-y-5 mt-4">
        <button
          type="button"
          onClick={() => {
            setStep("phone")
            setError(null)
          }}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          Changer de numéro
        </button>

        {info && (
          <div className="rounded-xl bg-[#25D366]/15 border border-[#25D366]/30 p-3 text-sm text-foreground">
            {info}
          </div>
        )}

        <div className="space-y-3">
          <Label htmlFor="wa-code" className="text-base">
            Entre le code à 6 chiffres reçu sur WhatsApp
          </Label>
          <div className="flex justify-center py-2">
            <InputOTP
              id="wa-code"
              maxLength={6}
              value={code}
              onChange={setCode}
              inputMode="numeric"
              autoFocus
              containerClassName="gap-2 sm:gap-3"
            >
              <InputOTPGroup className="gap-2 sm:gap-3">
                {[0, 1, 2, 3, 4, 5].map((i) => (
                  <InputOTPSlot
                    key={i}
                    index={i}
                    className="h-14 w-12 sm:h-16 sm:w-14 rounded-xl border text-2xl"
                  />
                ))}
              </InputOTPGroup>
            </InputOTP>
          </div>
        </div>

        {error && (
          <div className="rounded-xl bg-destructive/20 border border-destructive/30 p-3 text-sm text-destructive">
            {error}
          </div>
        )}

        <Button
          type="submit"
          className="w-full rounded-xl gradient-primary h-12 text-base"
          disabled={isLoading || code.length !== 6}
        >
          {isLoading ? "Vérification..." : "Vérifier et me connecter"}
        </Button>

        <button
          type="button"
          onClick={requestCode}
          disabled={isLoading}
          className="block w-full text-center text-sm text-[#06b6d4] hover:text-[#22d3ee] transition-colors disabled:opacity-50"
        >
          Renvoyer un code
        </button>
      </form>
    )
  }

  return (
    <form onSubmit={requestCode} className="space-y-5 mt-4">
      <div className="space-y-3">
        <Label htmlFor="wa-phone" className="text-base">
          Ton numéro WhatsApp
        </Label>
        <Input
          id="wa-phone"
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          placeholder="+33 6 12 34 56 78"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          required
          autoFocus
          className="rounded-xl h-12 text-base"
        />
        <p className="text-sm text-muted-foreground">
          Format international, avec l'indicatif du pays (ex. +33 pour la France).
        </p>
      </div>

      {error && (
        <div className="rounded-xl bg-destructive/20 border border-destructive/30 p-3 text-sm text-destructive">
          {error}
        </div>
      )}

      <Button
        type="submit"
        className="w-full rounded-xl gradient-primary h-12 text-base"
        disabled={isLoading || phone.trim().length === 0}
      >
        <MessageCircle className="h-5 w-5" aria-hidden="true" />
        {isLoading ? "Envoi en cours..." : "Recevoir un code"}
      </Button>
    </form>
  )
}
